import { useState } from 'react';
import type { LegalAction } from '../../api-client.js';
import { fmtClaim, type GbClaim, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// Quick chat — a structured phrase builder, Among Us style
// ---------------------------------------------------------------------------
// Safe by construction: you assemble a statement from the server's legal
// phrase set plus constrained parameters, so there is nothing to
// moderate. These statements are game events — a stated voting intention
// is scored against the ballot you actually cast.
//
// Every parameter the server can ask for is buildable here. It used to
// handle only a target seat and a bare value, so the phrases that name
// a specific BALL ("Teal is lying about their $5,000") were unreachable
// from the UI even though the engine offered them — the client sent no
// ballId and the submission was rejected.

type Slot =
  | { kind: 'target'; options: string[] }
  | { kind: 'target2'; options: string[] }
  | { kind: 'ball'; options: Array<{ ballId: string; claim: GbClaim }> }
  | { kind: 'value'; options: GbClaim[] }
  | { kind: 'collision'; options: Array<{ value: GbClaim; seatIds: string[] }> };

type PhraseGroup = { key: string; label: string; match: (id: string) => boolean };

const GROUPS: PhraseGroup[] = [
  { key: 'reply', label: 'Answer back', match: (id) => id.startsWith('reply_') },
  { key: 'intent', label: 'Voting', match: (id) => id.startsWith('intent_') },
  { key: 'accuse', label: 'Accuse', match: (id) => id.startsWith('accuse_') },
  {
    key: 'lie',
    label: 'Call a lie',
    match: (id) => id.startsWith('lie_call_') || id.startsWith('believe_')
  },
  { key: 'pact', label: 'Pacts', match: (id) => id.startsWith('pact_') },
  { key: 'final', label: 'The final', match: (id) => id.startsWith('final_') },
  { key: 'talk', label: 'Talk', match: (id) => id.startsWith('reassure_') || id.startsWith('talk_') }
];

type Schema = {
  targetSeatId?: { enum?: string[] };
  target2SeatId?: { bySeat?: Record<string, string[]> };
  ballId?: { bySeat?: Record<string, Array<{ ballId: string; claim: GbClaim }>> };
  value?: {
    enum?: GbClaim[];
    bySeat?: Record<string, GbClaim[]>;
    collisions?: Array<{ value: GbClaim; seatIds: string[] }>;
  };
};

export function QuickChatBuilder({
  actions,
  seats,
  yourSeatId,
  busy,
  onSay
}: {
  /** Server-supplied gb:chat:* descriptors with their choice schemas. */
  actions: LegalAction[];
  seats: GbSeatView[];
  yourSeatId: string | null;
  busy: boolean;
  onSay: (descriptorId: string, choices: Record<string, unknown>) => void;
}) {
  const [groupKey, setGroupKey] = useState('intent');
  const [pending, setPending] = useState<LegalAction | null>(null);
  // Parameters gathered so far for the pending phrase.
  const [choices, setChoices] = useState<Record<string, unknown>>({});

  const chatActions = actions.filter((action) => action.descriptorId.startsWith('gb:chat:'));
  if (chatActions.length === 0) return null;

  const phraseId = (action: LegalAction) => action.descriptorId.replace('gb:chat:', '');
  const schemaOf = (action: LegalAction) => (action.choiceSchema ?? {}) as Schema;
  const seatLabel = (seatId: string) =>
    seats.find((seat) => seat.seatId === seatId)?.label ?? seatId;

  const reset = () => {
    setPending(null);
    setChoices({});
  };

  /**
   * The next parameter the server still needs, given what has been
   * picked. Returning one slot at a time keeps the phone UI to a single
   * row of large targets instead of a form.
   */
  function nextSlot(action: LegalAction, picked: Record<string, unknown>): Slot | null {
    const schema = schemaOf(action);
    const target = picked['targetSeatId'] as string | undefined;

    // A same-claim callout is chosen as ONE thing — the collision — and
    // fills the value and both seats at once.
    if (schema.value?.collisions && !picked['value']) {
      return { kind: 'collision', options: schema.value.collisions };
    }
    if (schema.targetSeatId && !target) {
      return { kind: 'target', options: schema.targetSeatId.enum ?? [] };
    }
    if (schema.target2SeatId && !picked['target2SeatId']) {
      return {
        kind: 'target2',
        options: (target ? schema.target2SeatId.bySeat?.[target] : undefined) ?? []
      };
    }
    if (schema.ballId && !picked['ballId']) {
      return {
        kind: 'ball',
        options: (target ? schema.ballId.bySeat?.[target] : undefined) ?? []
      };
    }
    if (schema.value && !picked['value']) {
      const options = schema.value.bySeat
        ? ((target ? schema.value.bySeat[target] : undefined) ?? [])
        : (schema.value.enum ?? []);
      return { kind: 'value', options };
    }
    return null;
  }

  /** Add a parameter; send the moment nothing else is outstanding. */
  function advance(action: LegalAction, patch: Record<string, unknown>) {
    const next = { ...choices, ...patch };
    if (nextSlot(action, next) === null) {
      onSay(action.descriptorId, next);
      reset();
      return;
    }
    setChoices(next);
  }

  const group = GROUPS.find((candidate) => candidate.key === groupKey) ?? GROUPS[0]!;
  const groupActions = chatActions.filter((action) => group.match(phraseId(action)));

  // Second step: fill in whatever the phrase still needs.
  if (pending) {
    const slot = nextSlot(pending, choices);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-serif text-xs text-ec-face">{pending.label}</p>
          <button
            type="button"
            onClick={reset}
            className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim hover:text-ec-face"
          >
            back
          </button>
        </div>
        <p className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
          {slot?.kind === 'ball'
            ? 'which ball?'
            : slot?.kind === 'target2'
              ? 'and who else?'
              : slot?.kind === 'value' || slot?.kind === 'collision'
                ? 'which amount?'
                : 'who?'}
        </p>
        <div className="flex flex-wrap gap-1">
          {slot?.kind === 'target' &&
            slot.options
              .filter((seatId) => seatId !== yourSeatId)
              .map((seatId) => (
                <Chip key={seatId} busy={busy} onClick={() => advance(pending, { targetSeatId: seatId })}>
                  {seatLabel(seatId)}
                </Chip>
              ))}

          {slot?.kind === 'target2' &&
            slot.options
              .filter((seatId) => seatId !== yourSeatId)
              .map((seatId) => (
                <Chip
                  key={seatId}
                  busy={busy}
                  onClick={() => advance(pending, { target2SeatId: seatId })}
                >
                  {seatLabel(seatId)}
                </Chip>
              ))}

          {slot?.kind === 'ball' &&
            slot.options.map((ball) => (
              <Chip key={ball.ballId} busy={busy} onClick={() => advance(pending, { ballId: ball.ballId })}>
                <span className="tabular-nums">{fmtClaim(ball.claim)}</span>
              </Chip>
            ))}

          {slot?.kind === 'value' &&
            slot.options.map((value) => (
              <Chip key={String(value)} busy={busy} onClick={() => advance(pending, { value })}>
                <span className="tabular-nums">{fmtClaim(value)}</span>
              </Chip>
            ))}

          {/* A collision names the amount AND both seats in one press. */}
          {slot?.kind === 'collision' &&
            slot.options.map((entry) => (
              <Chip
                key={String(entry.value)}
                busy={busy}
                onClick={() =>
                  advance(pending, {
                    value: entry.value,
                    targetSeatId: entry.seatIds[0],
                    ...(entry.seatIds.length > 1 ? { target2SeatId: entry.seatIds[1] } : {})
                  })
                }
              >
                <span className="tabular-nums">{fmtClaim(entry.value)}</span>
                <span className="ml-1 text-ec-bone-dim">
                  {entry.seatIds.map(seatLabel).join(' + ')}
                </span>
              </Chip>
            ))}

          {(!slot || slot.options.length === 0) && (
            <p className="font-serif text-xs text-ec-bone-dim">Nothing to point at right now.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {GROUPS.map((candidate) => {
          const count = chatActions.filter((action) => candidate.match(phraseId(action))).length;
          if (count === 0) return null;
          return (
            <button
              key={candidate.key}
              type="button"
              onClick={() => setGroupKey(candidate.key)}
              aria-pressed={candidate.key === groupKey}
              className={[
                'min-h-[2rem] rounded-full border px-2 font-display text-[0.6rem] uppercase tracking-wide transition',
                candidate.key === groupKey
                  ? 'border-ec-brass bg-ec-brass/15 text-ec-brass'
                  : candidate.key === 'reply'
                    ? // Somebody has accused you and it is unanswered.
                      'border-ec-oxblood-2/70 text-ec-oxblood-2'
                    : 'border-ec-line text-ec-bone-dim hover:text-ec-face'
              ].join(' ')}
            >
              {candidate.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-1">
        {groupActions.map((action) => {
          const needsChoice = nextSlot(action, {}) !== null;
          return (
            <button
              key={action.descriptorId}
              type="button"
              disabled={busy}
              onClick={() => (needsChoice ? setPending(action) : onSay(action.descriptorId, {}))}
              className="min-h-[2.75rem] rounded border border-ec-line bg-ec-panel px-2 py-1 text-left font-serif text-xs text-ec-face transition hover:border-ec-brass/60 disabled:opacity-40"
            >
              {action.label}
              {needsChoice && <span className="ml-1 text-ec-bone-dim">…</span>}
            </button>
          );
        })}
        {groupActions.length === 0 && (
          <p className="font-serif text-xs text-ec-bone-dim">Nothing to say in this group.</p>
        )}
      </div>
    </div>
  );
}

function Chip({
  busy,
  onClick,
  children
}: {
  busy: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className="ec-btn min-h-[2.75rem] px-3 font-serif text-xs disabled:opacity-40"
    >
      {children}
    </button>
  );
}
